import { CloseOutlined, EditOutlined, TagsOutlined, DeleteOutlined } from '@ant-design/icons';
import { Col, Popover, Row, Space } from 'antd';
import { Components } from '@fasstech/flexiget';
import { allPass, always, compose, cond, equals, filter, isNil, path, pathEq, prop, propEq, propOr, propSatisfies, isNotNil } from 'ramda';
import { notEqual, propNotEq } from 'ramda-adjunct';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTreeContext } from '../../../../components/treeStructure/treeContext';
import useDocumentsTable from '../../../../hooks/useDocumentsTable';
import useActionGuard from '../../../../hooks/useActionGuard';
import SelectTags from '../../../docs/components/document/SelectTags';
import { renderDate } from '../../../docs/components/helpers/dates';
import { updateKeys } from '../../../../components/treeStructure/treeHelper';
import { setElementClass } from '../../../helpers/setElementClass';
import { useShowAction } from '../../../../hooks/useShowAction';
import ConfirmButton from '../../../../components/ConfirmButton';
import useOfferRole from '../../../../hooks/useOfferRole';
import { canEditOfferItem } from '../../../../components/users/utils/roles';
import DocumentDates from './DocumentDates';
import DocumentRights from './DocumentRights';

const DocumentTreeRow = ({
  folder,
  elementId,
  refRow,
  args,
  offerStatus,
  originalDocuments,
  setDocuments,
  refetch,
  setRefetchWidgets,
  isPublished,
  isReview,
  teams,
  userTeam,
  setIsLoadingOfferItem,
  setFullscreenLoading,
  setRefetchTags,
  reFetchFolders
}) => {
  const elementRef = useRef();
  const { showAction } = useShowAction(elementRef);

  const { offerId, scope } = args;
  const { userIsAdmin, userIsOfferEditor } = useOfferRole(offerId);
  const isDraft = equals(offerStatus, 'draft');
  const userCanEditDocuments = canEditOfferItem({
    userIsAdmin,
    userIsOfferEditor,
    scope,
    isDraft
  });
  const { removeTree, expandedKeys, setExpandedKeys, trees } = useTreeContext();
  const { documentPreviewUrl, deleteRawDocument } = useDocumentsTable();
  const { loading, run } = useActionGuard();

  const element = prop('element', folder);
  const documentName = prop('name', element);
  const isRawDocument = propEq('rawDocument', 'type')(element);
  const hasParentFolder = allPass([
    propSatisfies(isNotNil, 'parent'),
    always(elementId)
  ])(folder);

  const handleDeleteRawDocument = () => {
    run(`delete-raw-document-${elementId}`, (done) => {
      if (isNil(prop('treeId', element))) {
        const nodeKey = compose(
          path([0, 'key']),
          filter(pathEq(elementId, ['element', 'elmId']))
        )(trees);

        const updatedKeys = updateKeys(expandedKeys, [nodeKey], 'remove');

        setExpandedKeys(updatedKeys);
        deleteRawDocument(elementId, (ok) => {
          if (ok) setDocuments(filter(propNotEq(elementId, 'id')));
          done(ok);
        });
      } else {
        removeTree(prop('treeId', element), folder);
        deleteRawDocument(prop('elmId', element), (ok) => {
          if (ok) setDocuments(filter(propNotEq(prop('elmId', element), 'id')));
          done(ok);
        });
      }
    });
  };

  const handleDeleteFromDirectory = () => {
    removeTree(prop('treeId', element), folder, true);
  };

  useEffect(() => {
    if (documentPreviewUrl) {
      window.open(documentPreviewUrl);
    }
  }, [documentPreviewUrl]);

  const linkToDocument = cond([
    [() => originalDocuments && !isRawDocument, always(`/offre/${elementId}/editer`)],
    [() => isRawDocument, always(`/files/${elementId}`)],
    [
      () =>
        userCanEditDocuments ||
        notEqual(offerStatus, 'review'),
      always(`/offre/${offerId}/documents-importes/${elementId}/edition`)
    ]
  ])();

  const canRemoveFromFolder = hasParentFolder && userCanEditDocuments;

  return (
    <>
      <Col ref={refRow}>
        <Link to={linkToDocument} target={isRawDocument ? '_blank' : null}>
          {documentName}
        </Link>
      </Col>
      <Col ref={elementRef}>
        <Row gutter={6} className={`opacity-${showAction ? '100' : '0'}`}>
          <Col>
            <Space>
              {canRemoveFromFolder && (
                <Components.Tooltip
                  title="Retirer le document de l'arborescence"
                >
                  <CloseOutlined
                    onClick={handleDeleteFromDirectory}
                    className="text-black hover:text-flexi-grey btn-remove-item-root"
                  />
                </Components.Tooltip>
              )}

              {isRawDocument ? (
                <>
                  {userCanEditDocuments && (
                    <>
                      <Components.Tooltip
                        title="Modifier le nom du document"
                      >
                        <Link
                          to={`documents-importes/${elementId}/edition`}
                          className="text-black hover:text-flexi-grey"
                        >
                          <EditOutlined />
                        </Link>
                      </Components.Tooltip>
                      <Popover
                        trigger="click"
                        title=""
                        placement="bottom"
                        content={(
                          <SelectTags
                            offerId={offerId}
                            documentId={elementId}
                          />
                        )}
                      >
                        <Components.Tooltip title="Lier à un tag">
                          <TagsOutlined className="hover:text-flexi-grey" />
                        </Components.Tooltip>
                      </Popover>
                      <Components.Tooltip title="Supprimer le document">
                        <ConfirmButton
                          placement="left"
                          title="Êtes-vous sûr de vouloir supprimer ce document ?"
                          onConfirm={handleDeleteRawDocument}
                          buttonType="text"
                          sizeButton="small"
                          className="!w-3.5 !h-3.5"
                          icon={<DeleteOutlined className={`hover:text-flexidocs-red ${setElementClass(documentName, 'delete-document-button')}`} />}
                          loading={propOr(false, `delete-raw-document-${elementId}`, loading)}
                        />
                      </Components.Tooltip>
                    </>
                  )}
                </>
              ) : (
                <DocumentRights
                  doc={element}
                  setDocuments={setDocuments}
                  offerId={offerId}
                  scope={scope}
                  folder={folder}
                  refetch={refetch}
                  setRefetchWidgets={setRefetchWidgets}
                  isPublished={isPublished}
                  isReview={isReview}
                  documentName={documentName}
                  teams={teams}
                  userTeam={userTeam}
                  setIsLoadingOfferItem={setIsLoadingOfferItem}
                  setFullscreenLoading={setFullscreenLoading}
                  setRefetchTags={setRefetchTags}
                  reFetchFolders={reFetchFolders}
                />
              )}
            </Space>
          </Col>

          {element && element.dates && (
            <DocumentDates renderDate={renderDate} element={element} />
          )}
        </Row>
      </Col>
    </>
  );
};

export default DocumentTreeRow;
